import { SxProps, Theme } from "@mui/material";
import { AvailablePaymentCurrency, getField, Locales, formatCurrencyAmount, getDollarValue } from "@simplyk/common";

import { TicketingFormManager } from "../../../contexts/FormV2Context";

import { formatParticipantAnswers } from "./formatParticipantAnswers";
import { ShopCartLineItem } from "./ShopCartContext";

import { deriveVariantLabel } from "@/common/helpers/rates";
import { ParticipantAnswers, TicketPurchase } from "@/common/types/ticketing";
import { QuestionOutput, RateOutput } from "@/common/types/trpc";

// e.g. "Hoodie • Purple • L" with bulleted answers below ("Fletcher #34"); answers omitted if empty.
export interface CartRow {
  key: string;
  rateId: string;
  title: string;
  variantLabel: string;
  unitPrice: number;
  answers: string[];
  // Position of this ticket within the rate group; used to locate which participant entry to remove.
  ticketIndexWithinRate: number;
}

export const cartDeleteIconSx: SxProps<Theme> = (theme) => ({
  cursor: "pointer",
  borderRadius: "50%",
  padding: "4px",
  boxSizing: "content-box",
  color: theme.palette.forms?.text?.moderate,
  transition: "background-color 120ms ease-out, color 120ms ease-out",
  "&:hover": {
    backgroundColor: theme.palette.forms?.surface?.quiet,
    color: theme.palette.forms?.text?.intense,
  },
  "&:active": {
    backgroundColor: theme.palette.forms?.surface?.moderate,
  },
});

export const formatPrice = (amountInCents: number, currency: AvailablePaymentCurrency, locale: Locales): string => {
  return formatCurrencyAmount({
    value: getDollarValue(amountInCents),
    currency,
    locale,
  });
};

// Build one CartRow per purchased ticket, in lineItem order. e.g. quantity=3 → 3 rows.
export const buildCartRows = ({
  lineItems,
  validRatesSorted,
  questions,
  participants,
  ticketsPurchased,
  locale,
}: {
  lineItems: ShopCartLineItem[];
  validRatesSorted: RateOutput[];
  questions: QuestionOutput[];
  participants: ParticipantAnswers[];
  ticketsPurchased: TicketPurchase[] | undefined;
  locale: Locales;
}): CartRow[] => {
  return lineItems.flatMap((item) => {
    const rate = validRatesSorted.find((r) => r.id === item.parentRateId);
    return buildRowsForLineItem({
      item,
      rateTitle: getField(rate?.rateFields, locale)?.title ?? "",
      variantLabel: rate && item.childRateId ? deriveVariantLabel(item.childRateId, rate, locale) : "",
      unitPrice: getCartUnitPriceForRate(rate, ticketsPurchased),
      rateQuestions: collectQuestionsForRate(questions, item.parentRateId),
      itemParticipants: pickParticipantsForRate(participants, item.childRateId ?? item.parentRateId),
      locale,
    });
  });
};

export const getCartUnitPriceForRate = (
  rate: RateOutput | undefined,
  ticketsPurchased: TicketPurchase[] | undefined
): number => {
  if (!rate?.isPayWhatYouCan) {
    return rate?.amount ?? 0;
  }
  const parentTicket = (ticketsPurchased ?? []).find((t) => t.rateId === rate.id);
  const parentQuantity = parentTicket?.purchasedNumber ?? 0;
  const parentPwycAmount = parentTicket?.pwycAmount ?? 0;
  return parentQuantity > 0 ? parentPwycAmount / parentQuantity : parentPwycAmount;
};

const buildRowsForLineItem = ({
  item,
  rateTitle,
  variantLabel,
  unitPrice,
  rateQuestions,
  itemParticipants,
  locale,
}: {
  item: ShopCartLineItem;
  rateTitle: string;
  variantLabel: string;
  unitPrice: number;
  rateQuestions: QuestionOutput[];
  itemParticipants: ParticipantAnswers[];
  locale: Locales;
}): CartRow[] => {
  const itemRateId = item.childRateId ?? item.parentRateId;
  const rows: CartRow[] = [];
  for (let ticketIndex = 0; ticketIndex < item.quantity; ticketIndex++) {
    rows.push({
      key: `${itemRateId}-${ticketIndex}`,
      rateId: itemRateId,
      title: rateTitle,
      variantLabel,
      unitPrice,
      answers: formatParticipantAnswers(itemParticipants[ticketIndex], rateQuestions, locale),
      ticketIndexWithinRate: ticketIndex,
    });
  }
  return rows;
};

const collectQuestionsForRate = (questions: QuestionOutput[], parentRateId: string): QuestionOutput[] => {
  return questions.filter((q) => q.hasRate && q?.rateQuestions?.some((rq) => rq.rateId === parentRateId));
};

// Participants are appended to `customAnswersOfParticipants` in the order tickets are added,
// so the Nth match for a `rateId` corresponds to the Nth purchased ticket of that rate.
const pickParticipantsForRate = (participants: ParticipantAnswers[], rateId: string): ParticipantAnswers[] => {
  return participants.filter((p) => p.rateId === rateId);
};

interface DecrementResult {
  ticketIndex: number;
  decrementedTicket: TicketPurchase;
  nextPurchasedNumber: number;
}

// Decrement `purchasedNumber` for the form ticket matching `rateId`. Returns the resulting
// state for follow-up syncs (PWYC amount, participant answers), or `null` if no ticket exists.
export const decrementPurchasedNumberInForm = ({
  form,
  rateId,
}: {
  form: TicketingFormManager;
  rateId: string;
}): DecrementResult | null => {
  const allTickets = form.getValues("ticketsPurchased") ?? [];
  const ticketIndex = allTickets.findIndex((ticket) => ticket.rateId === rateId);
  if (ticketIndex < 0) {
    return null;
  }

  const decrementedTicket = { ...allTickets[ticketIndex] };
  const nextPurchasedNumber = Math.max(0, (decrementedTicket.purchasedNumber ?? 0) - 1);
  form.setValue(`ticketsPurchased.${ticketIndex}.purchasedNumber` as never, nextPurchasedNumber as never);

  return { ticketIndex, decrementedTicket, nextPurchasedNumber };
};

export interface PwycAmountUpdate {
  ticketIndex: number;
  pwycAmount: number | null;
}

export const computePwycAmountUpdatesAfterDecrement = ({
  ticketsPurchased,
  validRatesSorted,
  decrementedTicket,
  ticketIndex,
  nextPurchasedNumber,
}: {
  ticketsPurchased: TicketPurchase[];
  validRatesSorted: RateOutput[];
  decrementedTicket: TicketPurchase;
  ticketIndex: number;
  nextPurchasedNumber: number;
}): PwycAmountUpdate[] => {
  const parentPwycRate = findPwycParentRateFor(decrementedTicket.rateId, validRatesSorted);
  if (!parentPwycRate) {
    return [];
  }

  const updates: PwycAmountUpdate[] = [];
  const isVariantChildLine = decrementedTicket.rateId !== parentPwycRate.id;

  if (isVariantChildLine) {
    const parentTicket = ticketsPurchased.find((ticket) => ticket.rateId === parentPwycRate.id);
    const parentUnitAmount = parentTicket?.pwycAmount ?? 0;
    updates.push({ ticketIndex, pwycAmount: parentUnitAmount * nextPurchasedNumber });
  } else {
    const previousQty = decrementedTicket.purchasedNumber ?? 0;
    const previousPwycAmount = decrementedTicket.pwycAmount ?? 0;
    const unitAmount = previousQty > 0 ? previousPwycAmount / previousQty : previousPwycAmount;
    updates.push({ ticketIndex, pwycAmount: unitAmount * nextPurchasedNumber });
  }

  if (nextPurchasedNumber === 0) {
    const parentClearUpdate = computeParentPwycClearIfNoVariantLeft({ ticketsPurchased, parentPwycRate });
    if (parentClearUpdate) {
      updates.push(parentClearUpdate);
    }
  }

  return updates;
};

export const syncPwycAmountForVariantAfterDecrement = ({
  form,
  validRatesSorted,
  decrementedTicket,
  ticketIndex,
  nextPurchasedNumber,
}: {
  form: TicketingFormManager;
  validRatesSorted: RateOutput[];
  decrementedTicket: TicketPurchase;
  ticketIndex: number;
  nextPurchasedNumber: number;
}): void => {
  const updates = computePwycAmountUpdatesAfterDecrement({
    ticketsPurchased: form.getValues("ticketsPurchased") ?? [],
    validRatesSorted,
    decrementedTicket,
    ticketIndex,
    nextPurchasedNumber,
  });
  for (const update of updates) {
    form.setValue(`ticketsPurchased.${update.ticketIndex}.pwycAmount` as never, update.pwycAmount as never);
  }
};

const computeParentPwycClearIfNoVariantLeft = ({
  ticketsPurchased,
  parentPwycRate,
}: {
  ticketsPurchased: TicketPurchase[];
  parentPwycRate: RateOutput;
}): PwycAmountUpdate | null => {
  const childRateIds = new Set(
    ((parentPwycRate as RateOutput & { childRatesInfo?: Array<{ childRateId: string }> }).childRatesInfo ?? []).map(
      (child) => child.childRateId
    )
  );
  const anyOwnedLineRemaining = ticketsPurchased.some((ticket) => {
    const isOwnedRow = ticket.rateId === parentPwycRate.id || childRateIds.has(ticket.rateId);
    return isOwnedRow && (ticket.purchasedNumber ?? 0) > 0;
  });
  if (anyOwnedLineRemaining) {
    return null;
  }
  const parentIndex = ticketsPurchased.findIndex((ticket) => ticket.rateId === parentPwycRate.id);
  if (parentIndex < 0) {
    return null;
  }
  return { ticketIndex: parentIndex, pwycAmount: null };
};

// Returns the positions in `participants` whose entry matches `rateId`, in input order. `result[N]` is therefore the position of the Nth participant entry of that rate.
export const findParticipantIndicesForRate = (participants: ParticipantAnswers[], rateId: string): number[] => {
  return participants.flatMap((p, idx) => (p.rateId === rateId ? [idx] : []));
};

// Picks which participant entry to drop when the user removes a ticket. Prefers the Nth match
// (the row the user clicked), but falls back to the last when there are fewer participant entries
// than tickets — e.g. when some tickets of that rate have no answered questions, no participant
// entry was ever appended for them.
export const getIndexOfAnswerToRemove = (
  participantIndicesForRate: number[],
  ticketIndexWithinRate: number
): number | undefined => {
  return participantIndicesForRate[ticketIndexWithinRate] ?? participantIndicesForRate.at(-1);
};

// Keep `customAnswersOfParticipants` in sync with `ticketsPurchased`. Otherwise stale
// participant entries (left over from a previously higher quantity) flow into the submit
// payload and the server attributes multiple answers to the same product ticket.
export const removeNthParticipantAnswerFor = ({
  form,
  rateId,
  ticketIndexWithinRate,
}: {
  form: TicketingFormManager;
  rateId: string;
  ticketIndexWithinRate: number;
}): void => {
  const currentParticipants = form.getValues("customAnswersOfParticipants") ?? [];
  const participantIndicesForRate = findParticipantIndicesForRate(currentParticipants, rateId);
  const targetIndex = getIndexOfAnswerToRemove(participantIndicesForRate, ticketIndexWithinRate);
  if (targetIndex !== undefined) {
    form.setValue(
      "customAnswersOfParticipants",
      currentParticipants.filter((_, idx) => idx !== targetIndex)
    );
  }
};

// A rate is a "PWYC parent" for `rateId` when either the rate itself is `rateId` or one of its
// child variant rates is `rateId`, AND the rate is flagged `isPayWhatYouCan`.
const findPwycParentRateFor = (rateId: string, validRatesSorted: RateOutput[]): RateOutput | undefined => {
  return validRatesSorted.find(
    (rate) =>
      rate.isPayWhatYouCan && (rate.id === rateId || (rate.childRatesInfo ?? []).some((c) => c.childRateId === rateId))
  );
};
